/**
 * The workflow moves a reader may make on a programme, and how each is painted.
 *
 * @module components/shared/workflow-queue-actions
 */

import type { ProgrammeSummary, SessionContext } from "@/platform";
import { can } from "@/server/authorization";
import type { ReviewOutcome, WorkflowState } from "@/server/types";
import { isTransitionAllowed } from "@/server/workflow";

export interface WorkflowQueueAction {
  id: string;
  label: string;
  icon: string;
  nextState?: WorkflowState;
  outcome?: ReviewOutcome;
  requiresNote?: boolean;
  tone: "primary" | "outline" | "warning";
  blockedReason?: string;
}

/**
 * Whether the session holds the `reviewer` responsibility on this programme.
 *
 * `programme:review` (the broad `can()` grant) also passes for `client-admin`
 * and the `lead`/`approver` responsibilities, but the backend's review
 * transitions and outcomes (`isAssignedReviewer`, `requiredReviewerUserIds`)
 * accept only an assigned Reviewer. Gating the queue on the broader grant
 * offered buttons the server would then refuse.
 */
function isAssignedReviewer(session: SessionContext, programmeId: string): boolean {
  return session.subject.responsibilities.some(
    (assignment) =>
      assignment.programmeId === programmeId && assignment.responsibility === "reviewer",
  );
}

/**
 * Whether the caller is one of the current review round's frozen reviewers.
 *
 * `recordProgrammeReviewOutcome` (governance-operations.ts) accepts only a
 * userId already in `latestRound.requiredReviewerUserIds`, captured once at
 * submission (`prepareReviewSubmission`). A reviewer assigned to the
 * programme after that round started holds a live `reviewer` responsibility
 * but is not in this frozen set, and the server rejects their outcome
 * regardless, so outcome-recording actions gate on this, not on
 * {@link isAssignedReviewer}.
 */
function isCurrentRoundReviewer(
  session: SessionContext,
  currentRoundReviewerUserIds: readonly string[],
): boolean {
  return currentRoundReviewerUserIds.includes(session.subject.userId);
}

/** A Client Admin may send a programme back to Draft from any state the graph allows. */
export function mayReturnToDraft(
  session: SessionContext,
  programme: Pick<ProgrammeSummary, "id" | "workflowState">,
): boolean {
  return (
    can(session.subject, "programme:approve", { type: "programme", programmeId: programme.id }) &&
    isTransitionAllowed(programme.workflowState, "Draft")
  );
}

export function workflowQueueActions(
  session: SessionContext,
  programme: ProgrammeSummary,
  /** The current review round's frozen `requiredReviewerUserIds`, when one exists. */
  currentRoundReviewerUserIds: readonly string[] = [],
  /** Without an active reviewer a submission skips internal review. */
  hasInternalReviewer = true,
): WorkflowQueueAction[] {
  const resource = { type: "programme" as const, programmeId: programme.id };
  const mayWrite = can(session.subject, "programme:write", resource);
  const mayReview = isAssignedReviewer(session, programme.id);
  const mayRecordOutcome = isCurrentRoundReviewer(session, currentRoundReviewerUserIds);
  const mayApprove = can(session.subject, "programme:approve", resource);
  // Refused rather than absent: a reviewer assigned since the round opened is
  // still on this screen, and an empty list under "Available actions now" says
  // nothing about why.
  const outcomeRefusal = mayRecordOutcome
    ? undefined
    : "Only a reviewer named in the current review round can record an outcome.";

  switch (programme.workflowState) {
    case "Draft":
    case "Ready for Development":
      return [];
    case "In Development":
      return mayWrite
        ? [
            {
              id: "submit-review",
              label: hasInternalReviewer ? "Submit for internal review" : "Submit for QA approval",
              icon: "paper-plane-tilt",
              nextState: hasInternalReviewer ? "Awaiting Internal Review" : "Awaiting QA Approval",
              tone: "primary",
            },
          ]
        : [];
    case "Awaiting Internal Review":
      return mayReview
        ? [
            {
              id: "start-review",
              label: "Start review",
              icon: "magnifying-glass",
              nextState: "Internal Review",
              tone: "primary",
            },
          ]
        : [];
    case "Internal Review":
      return mayReview
        ? [
            {
              id: "comment",
              label: "Record comment",
              icon: "chat-circle-dots",
              outcome: "commented",
              requiresNote: true,
              tone: "outline",
              blockedReason: outcomeRefusal,
            },
            {
              id: "request-changes",
              label: "Request changes",
              icon: "arrow-u-down-left",
              nextState: "Changes Required",
              outcome: "change-request",
              requiresNote: true,
              tone: "warning",
              blockedReason: outcomeRefusal,
            },
            {
              id: "mark-reviewed",
              label: "Mark reviewed",
              icon: "check-circle",
              nextState: "Awaiting QA Approval",
              outcome: "approved",
              requiresNote: true,
              tone: "primary",
              blockedReason: outcomeRefusal,
            },
          ]
        : [];
    case "Changes Required":
      return mayWrite
        ? [
            {
              id: "resume-development",
              label: "Resume development",
              icon: "arrow-counter-clockwise",
              nextState: "In Development",
              tone: "primary",
            },
          ]
        : [];
    case "Awaiting QA Approval": {
      // Two different server grants: recording a change-request outcome goes
      // through recordProgrammeReviewOutcome, which only a reviewer in the
      // round's frozen requiredReviewerUserIds passes; approving goes through
      // the separate Client Admin-only programme:approve check. Neither
      // implies the other.
      const actions: WorkflowQueueAction[] = [];
      if (mayReview) {
        actions.push({
          id: "qa-request-changes",
          label: "Request changes",
          icon: "arrow-u-down-left",
          nextState: "Changes Required",
          outcome: "change-request",
          requiresNote: true,
          tone: "warning",
          blockedReason: outcomeRefusal,
        });
      }
      if (mayApprove) {
        actions.push({
          id: "approve",
          label: "Approve programme",
          icon: "seal-check",
          nextState: "Approved",
          outcome: "approved",
          requiresNote: true,
          tone: "primary",
        });
      }
      return actions;
    }
    default:
      return [];
  }
}

/** The `Button` emphasis an action takes, so no caller spells daisyUI classes. */
export function workflowActionAppearance(tone: WorkflowQueueAction["tone"]): {
  variant: "primary" | "outline";
  tone: "default" | "danger";
} {
  if (tone === "primary") {
    return { variant: "primary", tone: "default" };
  }
  return { variant: "outline", tone: tone === "warning" ? "danger" : "default" };
}
